import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { recomputeProjectTotal } from "@/hooks/use-budget-lines";

export interface ImportBudgetLineInput {
  phase: string;
  description: string;
  quantity_hours: number;
  amount: number;
  cost_type: "internal" | "external";
  estimated_cost?: number;
  /** Mal-id fra offer_line_templates hvis matcheren fant en match (VIB-584/586) */
  template_id?: string | null;
  /** Enhet (t/d/stk/fast) — fra mal eller brukervalg */
  unit?: string | null;
  /** Mengde — fra mal eller brukervalg (defaulter til quantity_hours hvis t/d) */
  quantity?: number | null;
  /** VIB-1021: tilvalg/opsjon — persisteres til budget_lines.is_optional. */
  is_optional?: boolean;
}

export interface ImportBudgetMetadataInput {
  /** Tilbudssum fra parser (sum etter overhead - rabatt) */
  offer_total: number;
  /** Overhead-prosent fra tilbudet (default 15) */
  overhead_pct: number;
  /** Rabatt-prosent fra tilbudet */
  discount_pct: number;
  /** Rabatt-beløp i kr (kan være null hvis kun pct er oppgitt) */
  discount_amount: number;
  /** Andel av tilbudssum som faktureres ved oppstart (default 60) */
  startup_invoice_pct: number;
  /** Andel ved sluttfaktura (default 40) */
  final_invoice_pct: number;
}

export interface ImportBudgetReport {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
  metadata_written: boolean;
}

interface ImportBudgetVars {
  project_id: string;
  lines: ImportBudgetLineInput[];
  metadata?: ImportBudgetMetadataInput | null;
}

/**
 * Idempotent insert/update av budget_lines mot et eksisterende prosjekt.
 *
 * Matcher hver input-linje mot eksisterende rader på (project_id, phase,
 * description). Hvis match: oppdaterer beløp/timer/cost_type. Hvis ikke:
 * inserter ny rad. Re-kjøring av samme PDF gir 0 created, N updated.
 *
 * Bruker i VIB-586 "Importer budsjett"-dialog på prosjektdetaljer.
 */
export function useImportBudget() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      project_id,
      lines,
      metadata,
    }: ImportBudgetVars): Promise<ImportBudgetReport> => {
      const report: ImportBudgetReport = {
        created: 0,
        updated: 0,
        skipped: 0,
        errors: [],
        metadata_written: false,
      };

      // Hent eksisterende linjer én gang for raskere lookups
      const { data: existing, error: existingErr } = await supabase
        .from("budget_lines")
        .select("id, phase, description")
        .eq("project_id", project_id);
      if (existingErr) {
        throw new Error(`Kunne ikke hente eksisterende budsjettlinjer: ${existingErr.message}`);
      }

      // Composite-key map: "phase|description" → eksisterende id
      const existingMap = new Map<string, string>();
      for (const row of existing ?? []) {
        if (row.phase && row.description) {
          existingMap.set(`${row.phase}|${row.description.trim().toLowerCase()}`, row.id);
        }
      }

      for (const line of lines) {
        if (!line.description?.trim()) {
          report.skipped++;
          continue;
        }
        const key = `${line.phase}|${line.description.trim().toLowerCase()}`;
        const existingId = existingMap.get(key);

        const payload = {
          project_id,
          phase: line.phase,
          description: line.description.trim(),
          quantity_hours: line.quantity_hours,
          amount: line.amount,
          cost_type: line.cost_type,
          estimated_cost: line.estimated_cost ?? 0,
          template_id: line.template_id ?? null,
          unit: line.unit ?? null,
          quantity: line.quantity ?? null,
          is_optional: line.is_optional ?? false,
        };

        if (existingId) {
          const { error: updateErr } = await supabase
            .from("budget_lines")
            .update(payload)
            .eq("id", existingId);
          if (updateErr) {
            report.errors.push(`Kunne ikke oppdatere "${line.description}": ${updateErr.message}`);
          } else {
            report.updated++;
          }
        } else {
          const { error: insertErr } = await supabase
            .from("budget_lines")
            .insert(payload);
          if (insertErr) {
            report.errors.push(`Kunne ikke opprette "${line.description}": ${insertErr.message}`);
          } else {
            report.created++;
          }
        }
      }

      // Upsert offer-metadata (overhead, rabatt, fakturasplit) hvis levert.
      // Tabellen har UNIQUE(project_id), så onConflict gir idempotent upsert.
      if (metadata) {
        const { error: metaErr } = await supabase
          .from("project_offer_metadata")
          .upsert(
            {
              project_id,
              offer_total: metadata.offer_total,
              overhead_pct: metadata.overhead_pct,
              discount_pct: metadata.discount_pct,
              discount_amount: metadata.discount_amount,
              startup_invoice_pct: metadata.startup_invoice_pct,
              final_invoice_pct: metadata.final_invoice_pct,
              extracted_at: new Date().toISOString(),
            },
            { onConflict: "project_id" },
          );
        if (metaErr) {
          report.errors.push(`Kunne ikke lagre tilbuds-metadata: ${metaErr.message}`);
        } else {
          report.metadata_written = true;
          // Hold projects.total_amount synkronisert (offer_total + mersalg).
          try {
            await recomputeProjectTotal(project_id);
          } catch (err) {
            report.errors.push(
              `Kunne ikke oppdatere total_amount: ${(err as Error).message}`,
            );
          }
        }
      }

      return report;
    },
    onSuccess: async (report, vars) => {
      await invalidateFor(qc, "budget_lines", { projectId: vars.project_id });
      if (report.metadata_written) {
        await invalidateFor(qc, "project_offer_metadata", { projectId: vars.project_id });
      }
      const summary: string[] = [];
      if (report.created > 0) summary.push(`${report.created} ny`);
      if (report.updated > 0) summary.push(`${report.updated} oppdatert`);
      if (report.skipped > 0) summary.push(`${report.skipped} hoppet over`);
      if (report.metadata_written) summary.push("overhead/rabatt lagret");
      const msg = summary.length > 0 ? `Budsjett importert: ${summary.join(", ")}` : "Ingen endringer";
      if (report.errors.length > 0) {
        toast.warning(`${msg} (${report.errors.length} feilet)`);
      } else {
        toast.success(msg);
      }
    },
    onError: (err) => {
      toast.error(`Kunne ikke importere budsjett: ${(err as Error).message}`);
    },
  });
}
